import { useEffect } from 'react';
import { Portal, EstateType } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { ShellProviderProps } from '@aviv-estimation-funnel/shells/src/app/factory';
import { Box } from '@gemini/core';
import { ArrowRightIcon } from '@gemini/icons';
import { Link } from '@gemini/ui';
import { sellerLeadValue } from '@mfe/app/services/sellerLeadValueApiRequests';
import { CustomEventService, IEvents } from '@mfe-helpers/customEvent';
import { addError } from '@mfe-helpers/datadog';
import {
    getIsSeller,
    getShortPhoneNumberRegionCode,
    getTimeToSell,
} from '@mfe-helpers/formHelpers';
import {
    composeMPUInteractionPayload,
    composeMPUPageViewPayload,
    composePageViewPayload,
} from '@mfe-helpers/tracking';
import EstimationResultBlock from '@mfe-presentation/components/EstimationResultBlock';
import EstimateFeedback from '@mfe-presentation/components/EstimateFeedback';
import WebToAppBanner from '@mfe-presentation/components/WebToAppBanner';
import Icon from '@mfe-presentation/components/Icon';
import PropertyValuation from '@mfe-presentation/components/PropertyValuation';
import FsrboPromotion from '@mfe-presentation/components/FsrboPromotion';
import { useAppContext } from '@mfe-presentation/contexts';
import { isAndroidWebView, isIOSWebView } from '@mfe-presentation/hooks/useStepUtils';
import { useSuccessSnackbar } from '@mfe-presentation/hooks/useSuccessSnackbar';
import { IEstimations } from '@mfe-types/context';
import { EAvCategory, EMonthlyPriceUpdateInteractionEvents } from '@mfe-types/tracking';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import { portalHasWebToAppBanner, isFrenchPortal } from '@mfe-helpers/portalHelpers';
import Animation from '@mfe-presentation/components/Animation';
import { ValuationType } from '@aviv-estimation-funnel/estimation-data-models/enums/valuation-type.enum';
import EstimationResultFloatingButton from '@mfe-presentation/components/EstimationResultFloatingButton';

const displayAgencyCard = (estimations: IEstimations, shellContext?: ShellProviderProps) => {
    if (shellContext?.mfes.agencyCard) {
        return <shellContext.mfes.agencyCard estimations={estimations} />;
    }
    return null;
};

const displayProfiCard = (estimations: IEstimations, shellContext?: ShellProviderProps) => {
    const propertyCharacteristics = estimations.propertyCharacteristics;
    if (
        shellContext?.mfes.profiCard &&
        propertyCharacteristics?.address?.postalCode &&
        propertyCharacteristics?.location.latitude &&
        propertyCharacteristics?.location.longitude
    ) {
        return (
            <shellContext.mfes.profiCard
                zipCode={propertyCharacteristics.address.postalCode}
                coordinates={{
                    latitude: propertyCharacteristics.location.latitude,
                    longitude: propertyCharacteristics.location.longitude,
                }}
            />
        );
    }
    return null;
};

const displaySoldPropertiesCard = (
    estimations: IEstimations,
    shellContext?: ShellProviderProps,
) => {
    if (shellContext?.mfes.soldPropertiesCard) {
        return <shellContext.mfes.soldPropertiesCard estimations={estimations} />;
    }

    return null;
};

const displayAgentsList = (
    formValues: Record<string, any>,
    estimations: IEstimations,
    shellContext?: ShellProviderProps,
) => {
    if (shellContext?.mfes.agentsList) {
        const agentsListProps = {
            email: formValues.email,
            ownerObjective: estimations.intent?.ownerObjective,
            estimationAgentsCorrelationId: estimations.id,
            zip: estimations.propertyCharacteristics?.address.postalCode,
            sellPrice: estimations.valuation.sellPrice,
            previousEstimationId: estimations.previousEstimationId,
        };

        return <shellContext.mfes.agentsList agentsListProps={agentsListProps} />;
    }
    return null;
};

const Headline = () => {
    const { t } = useTranslation(NAME_SPACE);

    return (
        <Box
            testId="headline"
            as="header"
            textAlign={{
                'breakpoint.base': 'left',
                'breakpoint.xs': 'center',
            }}
        >
            <Box
                as="h1"
                typography={{
                    'breakpoint.base': 'typography.headline.24.bold',
                    'breakpoint.xs': 'typography.headline.28.bold',
                }}
            >
                {t('step_results.header.title')}
            </Box>
            <Box as="p" typography="typography.body.14.regular" testId="sub-headline">
                {/* eslint-disable-next-line react/no-danger */}
                <div dangerouslySetInnerHTML={{ __html: t(`step_results.header.subtitle`) }} />
            </Box>
        </Box>
    );
};

const EstateTypeIconBlock = () => {
    const { formValues, useShellContext, portal } = useAppContext();
    const shellContext = useShellContext();

    const apartmentMedia = isFrenchPortal(portal) ? (
        <Icon data-testid="apartment-icon" width="72" height="72" name="apartment" />
    ) : (
        <Box width="72px" height="72px">
            <Animation
                key="apartment"
                name="apartment"
                data={shellContext?.animations?.apartment}
                loop={false}
            />
        </Box>
    );

    const houseMedia = isFrenchPortal(portal) ? (
        <Icon data-testid="house-icon" width="72" height="72" name="house" />
    ) : (
        <Box width="72px" height="72px">
            <Animation
                key="house"
                name="house"
                data={shellContext?.animations?.house}
                loop={false}
            />
        </Box>
    );

    return (
        <Box>{formValues.estateType === EstateType.Apartment ? apartmentMedia : houseMedia}</Box>
    );
};

const EstimationResult = () => {
    const { estimations, formValues, portal, maxStepIndex, useShellContext, isFromUpdatePage } =
        useAppContext();
    const { t } = useTranslation(NAME_SPACE);

    const shellContext = useShellContext();

    const callSellerLeadValue = async () => {
        const {
            phoneNumberRegionCode,
            postalCode: propertyPostalCode,
            ownerObjective,
        } = formValues;
        const estimatedPropertyPrice = estimations?.valuation.sellPrice;
        /* TODO isDuplicate description: The same project was recently estimated and the value should be 0
        For the moment, we need more information to know if an estimation already exists*/
        const isDuplicate = false;
        const propertyShortRegionCode = getShortPhoneNumberRegionCode(phoneNumberRegionCode);
        const timeToSell = getTimeToSell(ownerObjective);
        const isSeller = getIsSeller(ownerObjective);

        try {
            if (
                propertyShortRegionCode &&
                propertyPostalCode &&
                typeof estimatedPropertyPrice === 'number'
            )
                await sellerLeadValue({
                    isSeller,
                    isDuplicate,
                    propertyCountry: propertyShortRegionCode,
                    propertyPostalCode,
                    ...(timeToSell && { timeToSell }),
                    estimatedPropertyPrice,
                });
        } catch (e) {
            addError(e);
        }
    };

    useEffect(() => {
        const pageViewPayload = isFromUpdatePage
            ? composeMPUPageViewPayload({
                  pageTitle: 'estimation_result',
                  formValues,
                  estimation: estimations,
                  portal,
                  category: EAvCategory.Estimate,
              })
            : composePageViewPayload({
                  pageTitle: 'estimate_confirmation',
                  formValues,
                  estimation: estimations,
                  portal,
                  stepIndex: maxStepIndex + 1,
              });

        CustomEventService.fire(IEvents.SEND_PAGE_VIEW, pageViewPayload);

        if (isFromUpdatePage) {
            CustomEventService.fire(
                IEvents.SEND_TRACKING_EVENT,
                composeMPUInteractionPayload({
                    eventName: EMonthlyPriceUpdateInteractionEvents.Confirmation,
                    formValues,
                    estimation: estimations,
                    category: EAvCategory.Estimate,
                    portal,
                }),
            );
        }

        if (process.env['NX_SELLER_LEAD_VALUE_API_SUPPORTED'] === 'true') {
            callSellerLeadValue();
        }

        if (estimations.id) {
            /*
                window.webkit.messageHandlers.wlefSendEstimationID and
                window.webkit.wlefSendEstimationID.postMessage
                are two methods created by the Mobile Experience team
                to communicate to IOS and Android application the estimation id
                when the estimation funnel is displayed in a webview
            */
            let postMessage;
            if (isIOSWebView()) {
                // @ts-expect-error window?.webkit
                postMessage = window?.webkit?.messageHandlers?.wlefSendEstimationID?.postMessage;
            } else if (isAndroidWebView()) {
                // @ts-expect-error  window?.webkit
                postMessage = window?.webkit?.wlefSendEstimationID?.postMessage;
            }
            if (typeof postMessage === 'function') postMessage(estimations.id);
        }
    }, []);

    const agencyCard = displayAgencyCard(estimations, shellContext);
    const agentsList = displayAgentsList(formValues, estimations, shellContext);
    const profiCard = displayProfiCard(estimations, shellContext);
    const soldPropertiesCard = displaySoldPropertiesCard(estimations, shellContext);

    const showPropertyValuationRent = Boolean(estimations?.valuation?.rentPrice);

    useSuccessSnackbar();

    return (
        <Box
            as="section"
            flexDirection="column"
            display="flex"
            alignItems="center"
            paddingHorizontal="spacing.16"
            width="100%"
            gap="spacing.40"
        >
            <Headline />
            <Box
                display="flex"
                alignItems="flex-start"
                typography="typography.body.12.regular"
                minWidth="288px"
                width="100%"
                flexDirection={{
                    'breakpoint.base': 'column',
                    'breakpoint.sm': 'row',
                }}
            >
                <Box
                    borderWidth="borderWidth.1"
                    borderStyle="solid"
                    borderColor="color.border.light.default"
                    borderRadius="radius.8"
                    paddingVertical="spacing.24"
                    width="100%"
                    flexBasis={showPropertyValuationRent ? '50%' : '100%'}
                >
                    <Box display="flex" alignItems="center" flexDirection="column">
                        <EstateTypeIconBlock />
                    </Box>
                    <PropertyValuation type="sell" isCompactMode={showPropertyValuationRent} />
                    <EstimateFeedback type={ValuationType.Sell} />
                </Box>
                {showPropertyValuationRent && (
                    <Box
                        borderWidth="borderWidth.1"
                        borderStyle="solid"
                        borderColor="color.border.light.default"
                        borderRadius="radius.8"
                        width="100%"
                        flexBasis="50%"
                        paddingVertical="spacing.24"
                        marginLeft={{
                            'breakpoint.base': 'spacing.none',
                            'breakpoint.sm': 'spacing.24',
                        }}
                        marginTop={{
                            'breakpoint.base': 'spacing.24',
                            'breakpoint.sm': 'spacing.none',
                        }}
                    >
                        <Box display="flex" alignItems="center" flexDirection="column">
                            <EstateTypeIconBlock />
                        </Box>
                        <PropertyValuation type="rent" isCompactMode />
                        <EstimateFeedback type={ValuationType.Rent} />
                    </Box>
                )}
            </Box>
            {showPropertyValuationRent && (
                <Box
                    color="color.content.light.default"
                    paddingHorizontal={{
                        'breakpoint.base': 'spacing.12',
                        'breakpoint.sm': 'spacing.24',
                    }}
                    textAlign="left"
                >
                    <Box typography="typography.body.14.regular">
                        {t(`step_results.resultCard.info.rent`)}
                    </Box>
                </Box>
            )}
            {agencyCard && <EstimationResultBlock>{agencyCard}</EstimationResultBlock>}
            {isFrenchPortal(portal) && (
                <EstimationResultBlock>
                    <FsrboPromotion />
                </EstimationResultBlock>
            )}
            {soldPropertiesCard && (
                <EstimationResultBlock>{soldPropertiesCard}</EstimationResultBlock>
            )}
            {(agentsList || profiCard) && (
                <EstimationResultBlock>
                    {agentsList}
                    {profiCard}
                </EstimationResultBlock>
            )}
            {portal === Portal.ImmoWelt && (
                <EstimationResultBlock>
                    <FsrboPromotion />
                </EstimationResultBlock>
            )}
            {portalHasWebToAppBanner(portal) && (
                <EstimationResultBlock>
                    <WebToAppBanner />
                </EstimationResultBlock>
            )}
            <Link
                href={process.env.NX_PORTAL_URL as string}
                icon={ArrowRightIcon}
                iconPosition="end"
            >
                {t('common.button.goToHomepage')}
            </Link>
            <EstimationResultFloatingButton />
        </Box>
    );
};

export default EstimationResult;
